import api from './api';

export interface AccessLog {
  id: number;
  sampleId: number | null;
  userId: number | null;
  action: string;
  path: string | null;
  ipAddress: string;
  userAgent: string | null;
  accessTime: string;
  sessionId: string | null;
  isPublicAccess: boolean;
  createdAt: string;
  updatedAt: string;
  // 法的証拠対応フィールド
  httpMethod: string | null;
  statusCode: number | null;
  durationMs: number | null;
  referer: string | null;
  countryCode: string | null;
  appSurface: string | null;
  clientEnvironment: string | null;
  ipChain: string | null;
  requestId: string | null;
  usernameSnapshot: string | null;
  userEmailSnapshot: string | null;
  sampleNameSnapshot: string | null;
  sample?: {
    id: number;
    name: string;
  } | null;
  user?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface AccessLogListParams {
  page?: number;
  limit?: number;
  sampleId?: number;
  userId?: number;
  action?: string;
  path?: string;
  isPublicAccess?: boolean;
  startDate?: string;
  endDate?: string;
  statusCode?: number;
  httpMethod?: string;
  requestId?: string;
  appSurface?: string;
}

export interface AccessLogListResponse {
  success: boolean;
  data: AccessLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AccessLogStatistics {
  total: number;
  byAction: Array<{ action: string; count: number }>;
  byPublicAccess: Array<{ isPublicAccess: boolean; count: number }>;
  byUser: Array<{
    userId: number | null;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    usernameSnapshot: string | null;
    count: number;
  }>;
  timeSeries: Array<{ date: string; count: number }>;
  recentActivity: AccessLog[];
  byPageView: Array<{ path: string; count: number }>;
  byCountry: Array<{ countryCode: string; count: number }>;
  byApp: Array<{
    appSurface: string;
    viewCount: number;
    totalDurationMs: number;
    engagementCount: number;
    avgDurationMs: number;
  }>;
  byEnvironment: Array<{ environment: string; count: number }>;
}

export interface AccessLogStatisticsResponse {
  success: boolean;
  data: AccessLogStatistics;
}

export interface ConversionFunnelData {
  totalUniqueIps: number;
  topOnlyCount: number;
  registerPageViewedCount: number;
  termsPrivacyOnlyCount: number;
}

export interface ConversionFunnelResponse {
  success: boolean;
  data: ConversionFunnelData;
}

export const accessLogService = {
  /**
   * アクセスログ一覧を取得（管理者のみ）
   */
  async list(params: AccessLogListParams = {}): Promise<AccessLogListResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', String(params.page));
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.sampleId) queryParams.append('sampleId', String(params.sampleId));
    if (params.userId) queryParams.append('userId', String(params.userId));
    if (params.action) queryParams.append('action', params.action);
    if (params.path) queryParams.append('path', params.path);
    if (params.isPublicAccess !== undefined)
      queryParams.append('isPublicAccess', String(params.isPublicAccess));
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.statusCode) queryParams.append('statusCode', String(params.statusCode));
    if (params.httpMethod) queryParams.append('httpMethod', params.httpMethod);
    if (params.requestId) queryParams.append('requestId', params.requestId);
    if (params.appSurface) queryParams.append('appSurface', params.appSurface);

    const response = await api.get<AccessLogListResponse>(`/access-logs?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * SPA ページ閲覧（認証不要・全アプリルート）
   */
  async recordPublicPageView(
    path: string,
    options?: { clientEnvironment?: string }
  ): Promise<void> {
    try {
      await api.post('/access-logs/public-page-view', {
        path,
        event: 'view',
        clientEnvironment: options?.clientEnvironment,
      });
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[accessLog] recordPublicPageView failed', err);
      }
    }
  },

  async recordPageEngagement(
    path: string,
    durationMs: number,
    options?: { clientEnvironment?: string; beacon?: boolean }
  ): Promise<void> {
    const body = {
      path,
      event: 'engagement',
      durationMs,
      clientEnvironment: options?.clientEnvironment,
    };
    try {
      if (options?.beacon && typeof navigator.sendBeacon === 'function') {
        const base =
          import.meta.env.VITE_API_URL ||
          (import.meta.env.DEV ? '/api' : '/api');
        const url = `${base.replace(/\/$/, '')}/access-logs/public-page-view`;
        navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: 'application/json' }));
        return;
      }
      await api.post('/access-logs/public-page-view', body);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[accessLog] recordPageEngagement failed', err);
      }
    }
  },

  async getStatistics(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<AccessLogStatisticsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get<AccessLogStatisticsResponse>(
      `/access-logs/statistics?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * 転換ファネル（IPベース）を取得（管理者のみ）
   * サーバー側 15s statement_timeout に合わせ、クライアントは 20s で打ち切る。
   */
  async getConversionFunnel(params: {
    startDate: string;
    endDate: string;
  }): Promise<ConversionFunnelResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    const response = await api.get<ConversionFunnelResponse>(
      `/access-logs/conversion-funnel?${queryParams.toString()}`,
      { timeout: 20000 }
    );
    return response.data;
  },
};
